import React from 'react';
import Chart from 'react-apexcharts';
import { Parser } from 'expr-eval';
import { alignedBox } from './AlignedBox';
import { denormalizeFieldName } from './utils';
import { generateColors } from './colors';
import { actualCurrencyFormatter } from './formatters';

export const groupDataForGroupedColumnChart = (data, xField, yField, pivotField, parsedFields) => {
  const parser = new Parser();
  const totalsByCategory = {};
  const groupedData = {};
  const allPivots = new Set(data.map(row => row[pivotField]));
  const allCategories = new Set(data.map(row => row[xField]));

  const yFieldDef = parsedFields.find(f => f.field === yField);
  let evaluateRow;

  if (yFieldDef?.isCalculated && yFieldDef.rawExpression) {
    const outerAggregateMatch = yFieldDef.rawExpression.match(/^\s*(sum|ave|min|max|count_distinct)\((.+)\)\s*$/i);
    const rowLevelExpr = outerAggregateMatch ? outerAggregateMatch[2] : yFieldDef.rawExpression;
    const expr = parser.parse(rowLevelExpr);
    evaluateRow = (row) => {
      try {
        return expr.evaluate(row);
      } catch (e) {
        console.warn(`Failed to evaluate expression for ${yField}:`, e);
        return 0;
      }
    };
  } else {
    evaluateRow = (row) => row[yField] ?? 0;
  }

  allPivots.forEach(pivot => {
    groupedData[pivot] = {};
    allCategories.forEach(category => {
      groupedData[pivot][category] = 0;
    });
  });

  data.forEach((row) => {
    const key = row[xField] || 'Unknown';
    const pivot = row[pivotField];
    const value = evaluateRow(row);

    if (!groupedData[pivot]) {
      groupedData[pivot] = {};
    }
    totalsByCategory[key] = (totalsByCategory[key] || 0) + value;
    groupedData[pivot][key] = (groupedData[pivot][key] || 0) + value;
  });

  return {
    groupedData,
    totalsByCategory,
  }
};

const PREFERRED_ORDER = ['Fast', 'Normal', 'Slow', 'Out of Stock'];

export const GroupedColumnChart = ({ title, data, xField, yField, pivotField, parsedFields }) => {
  const categories = [...new Set(data.map(row => row[xField]))];
  const pivotValues = [...new Set(data.map(row => row[pivotField]))];

  const { groupedData, totalsByCategory } = groupDataForGroupedColumnChart(data, xField, yField, pivotField, parsedFields);

  const categoriesWithValues = categories.reduce((acc, cat) => {
    if (!totalsByCategory[cat]) return acc;
    return [...acc, cat];
  }, []);
  categoriesWithValues.sort((a, b) => {
    if (a < b) return -1;
    return a > b ? 1 : 0
  });

  const series = pivotValues.map((pivot) => ({
    name: pivot,
    data: categoriesWithValues.map(cat => groupedData[pivot]?.[cat] ?? 0),
  }));

  const orderedSeries = [...series].sort((a, b) => {
    const indexA = PREFERRED_ORDER.indexOf(a.name);
    const indexB = PREFERRED_ORDER.indexOf(b.name);
    // anything not in preferred list gets push to the end
    return (indexA === -1 ? 99 : indexA) - (indexB === -1 ? 99 : indexB);
  });

  const colors = generateColors(pivotValues.length);

  const options = {
    chart: {
      type: 'bar',
      stacked: true,
      stackType: '100%',
    },
    legend: {
      show: true,
      position: 'right',
      horizontalAlign: 'left',
      fontSize: '10px',
      markers: {
        width: 10,
        height: 10,
        radius: 50,
      },
      labels: {
        colors,
      },
    },
    colors,
    xaxis: {
      categories: categoriesWithValues,
      labels: {
        style: {
          fontSize: '10px',
        },
      },
      title: {
        text: `${denormalizeFieldName(xField)}`,
      },
    },
    yaxis: {
      max: 100,
      tickAmount: 2,
      labels: {
        style: {
          fontSize: '10px',
        },
        formatter: (val) => `${val.toFixed(1)}%`,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '60%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      y: {
        formatter: actualCurrencyFormatter,
      },
    },
  };

  return alignedBox(title, (
    <Chart options={options} series={orderedSeries} type="bar" />
  ));
};
